import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import {
  AdminPlanCohortPurseMappingDetail,
  AdminPlanCohortPurseMappingRow,
  adminPlanCohortPurseMappingApi,
  UpdateMappingPayload,
} from '../../../../api/adminPlanCohortPurseMappingApi';
import { paths } from '../../../../routes/paths';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';

import MappingVersionHistoryDrawer from './MappingVersionHistoryDrawer';
import PublishConfirmationDialog, {
  PublishEntity,
} from '../tasks/PublishConfirmationDialog';
import { WriteButton } from '../../../../components/ui';

interface FormState {
  allocation_percent: number;
  active: boolean;
}

function formFromDetail(d: AdminPlanCohortPurseMappingDetail): FormState {
  return {
    allocation_percent: d.allocation_percent,
    active: !!d.active,
  };
}

function statusChipProps(status: AdminPlanCohortPurseMappingRow['status']): {
  label: string;
  bg: string;
  color: string;
} {
  if (status === 'unpublished')
    return { label: 'Unpublished', bg: colors.warningBgStrong, color: colors.warningText };
  if (status === 'in_sync')
    return { label: 'In sync', bg: colors.successBgStrong, color: colors.successText };
  return { label: 'Never published', bg: colors.bgMuted, color: colors.textSecondary };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const SectionLabel: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'Manrope, sans-serif',
        color: colors.adminAccent,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: colors.textTertiary, mt: 0.25 }}>{hint}</Typography>
    )}
  </Box>
);

const MappingEdit: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionConflict, setVersionConflict] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });
  const initialFormRef = useRef<FormState | null>(null);

  const detailQuery = useQuery<AdminPlanCohortPurseMappingDetail>({
    queryKey: ['admin', 'plan-cohort-purse', code],
    queryFn: () => adminPlanCohortPurseMappingApi.get(code),
    enabled: !!code,
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    const next = formFromDetail(detailQuery.data);
    setForm(next);
    initialFormRef.current = next;
    setBaseVersion(detailQuery.data.current_version);
  }, [detailQuery.data]);

  const isDirty = useMemo(() => {
    if (!form || !initialFormRef.current) return false;
    const a = form;
    const b = initialFormRef.current;
    return a.allocation_percent !== b.allocation_percent || a.active !== b.active;
  }, [form]);

  const validPct =
    form != null &&
    Number.isFinite(form.allocation_percent) &&
    form.allocation_percent >= 0 &&
    form.allocation_percent <= 100;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form || baseVersion == null) throw new Error('No form state');
      if (!validPct) throw new Error('allocation_percent must be between 0 and 100');
      const payload: UpdateMappingPayload = {
        base_version: baseVersion,
        allocation_percent: form.allocation_percent,
      };
      return adminPlanCohortPurseMappingApi.update(code, payload);
    },
    onSuccess: (row) => {
      setVersionConflict(null);
      setEditMode(false);
      setSnackbar({ open: true, msg: `Saved v${row.current_version}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', 'list'] });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.error === 'version_conflict') {
        setVersionConflict(data.current_version);
      } else {
        setSnackbar({
          open: true,
          msg: err?.message || 'Save failed — try again.',
          sev: 'error',
        });
      }
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      if (baseVersion == null) throw new Error('No base version');
      return adminPlanCohortPurseMappingApi.update(code, {
        base_version: baseVersion,
        active: true,
      });
    },
    onSuccess: (row) => {
      setReactivateOpen(false);
      setSnackbar({ open: true, msg: `Reactivated ${row.mapping_code}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', 'list'] });
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Reactivate failed — try again.', sev: 'error' });
    },
  });

  const handleCancelEdit = useCallback(() => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    if (initialFormRef.current) setForm(initialFormRef.current);
    setEditMode(false);
    setVersionConflict(null);
  }, [isDirty]);

  const handleReload = useCallback(async () => {
    if (isDirty && !window.confirm('Discard local changes and reload the latest version?')) return;
    setVersionConflict(null);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', code] });
    setEditMode(false);
  }, [isDirty, queryClient, code]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      const isInputTarget = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const cmd = ev.metaKey || ev.ctrlKey;

      if (cmd && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (editMode && isDirty && !saveMutation.isPending && validPct) saveMutation.mutate();
        return;
      }
      if (!isInputTarget) {
        if (ev.key === 'e' && !editMode) {
          ev.preventDefault();
          setEditMode(true);
        } else if (ev.key === 'v') {
          ev.preventDefault();
          setHistoryOpen(true);
        } else if (ev.key === 'Escape' && editMode) {
          handleCancelEdit();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, isDirty, saveMutation, handleCancelEdit, validPct]);

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.adminAccent }} />
      </Box>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            onClick={() => navigate(paths.admin.globalCatalog.planCohortPurseMappings())}
            sx={{ color: colors.errorText }}
          >
            Back to list
          </Button>
        }
      >
        Couldn&apos;t load mapping <code>{code}</code>.
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const cp = statusChipProps(detail.status);
  const platformVersion = detail.platform_mapping_id == null ? null : detail.current_version;
  const hasUnpublished = platformVersion != null && detail.current_version > platformVersion;
  const isInactive = !detail.active;

  return (
    <Box sx={{ maxWidth: 1200, pb: editMode ? 10 : 4 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1.5,
        }}
      >
        <MuiLink
          onClick={() => navigate(paths.admin.home())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mr: 0.5 }}
        >
          Admin
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.root())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Global Catalog
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.planCohortPurseMappings())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Plan-Cohort-Purse
        </MuiLink>
        /
        <span style={{ marginLeft: 4, fontFamily: 'Fira Code, monospace', color: colors.textPrimary }}>
          {detail.mapping_code}
        </span>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              ...typo.pageTitle,
              ...typo.mono,
              fontSize: 28,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {detail.plan_code} · {detail.cohort_code} → {detail.purse_code}
          </Typography>
          <Chip
            label={cp.label}
            size="small"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: cp.bg,
              color: cp.color,
              borderRadius: '6px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
            sx={secondaryButtonSx}
          >
            History <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>(v)</Box>
          </Button>
          {!editMode && !isInactive && (
            <WriteButton
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              sx={primaryButtonSx}
            >
              Edit <Box component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5 }}>(e)</Box>
            </WriteButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 3,
          ...typo.smallText,
          color: hasUnpublished ? colors.warningText : colors.textSecondary,
        }}
      >
        <span>Current version: v{detail.current_version}</span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <span>Effective: {detail.effective_date}</span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <span>
          {platformVersion != null
            ? `Last published: v${platformVersion} on ${formatDate(detail.updated_at)} by ${detail.updated_by ?? '—'}`
            : 'Never published'}
        </span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <Tooltip title="Tenant usage scan lands in a later round">
          <Button
            size="small"
            startIcon={<GroupIcon sx={{ fontSize: 14 }} />}
            onClick={() =>
              setSnackbar({ open: true, msg: 'Tenant usage scan lands in a later round', sev: 'info' })
            }
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 600,
              color: 'inherit',
              p: 0,
              minWidth: 0,
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Used by 0 tenants
          </Button>
        </Tooltip>
      </Box>

      {!detail.active && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <WriteButton
              size="small"
              onClick={() => setReactivateOpen(true)}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Reactivate
            </WriteButton>
          }
        >
          This mapping is inactive. It&apos;s hidden from tenants and cannot be edited.
        </Alert>
      )}

      {hasUnpublished && detail.active && (
        <Paper
          role="status"
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            mb: 3,
            p: 2,
            borderRadius: '8px',
            backgroundColor: colors.warningBg,
            borderLeft: `2px solid ${colors.warningIcon}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberIcon sx={{ color: colors.warningIcon, fontSize: 20 }} />
          <Typography sx={{ fontSize: 13, color: colors.warningText, flex: 1 }}>
            <strong>Unpublished changes.</strong> Tenants are still seeing v{platformVersion}.
            Publish this mapping to roll it out.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <WriteButton
              variant="contained"
              size="small"
              sx={{
                ...primaryButtonSx,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
                '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
              }}
              onClick={() => setPublishOpen(true)}
            >
              Publish this entity
            </WriteButton>
          </Box>
        </Paper>
      )}

      {versionConflict != null && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <Button size="small" onClick={handleReload}>
              Reload
            </Button>
          }
        >
          Someone else saved v{versionConflict} while you were editing.
        </Alert>
      )}

      {form && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
            p: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 4,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <Box>
              <SectionLabel
                label="Composite key"
                hint="Plan + cohort + purse + effective_date can't change after creation. Create a new mapping instead."
              />
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  size="small"
                  label="plan_code"
                  value={detail.plan_code}
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                    ),
                    sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
                  }}
                />
                <TextField
                  size="small"
                  label="cohort_code"
                  value={detail.cohort_code}
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                    ),
                    sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
                  }}
                />
                <TextField
                  size="small"
                  label="purse_code"
                  value={detail.purse_code}
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                    ),
                    sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
                  }}
                />
                <TextField
                  size="small"
                  label="effective_date"
                  value={detail.effective_date}
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                    ),
                    sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <SectionLabel
                label="Allocation"
                hint="0 – 100. Sibling mappings for the same plan+cohort+effective_date should sum to ≤ 100."
              />
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  size="small"
                  type="number"
                  label="allocation_percent"
                  value={form.allocation_percent}
                  onChange={(e) =>
                    setForm({ ...form, allocation_percent: Number(e.target.value) })
                  }
                  disabled={!editMode}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  error={editMode && !validPct}
                  helperText={
                    editMode
                      ? validPct
                        ? 'Valid.'
                        : 'Must be between 0 and 100.'
                      : undefined
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={form.active}
                    disabled={!editMode}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  <Typography sx={{ fontSize: 13 }}>Active</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {editMode && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            zIndex: 10,
            backgroundColor: colors.bgWhite,
            borderTop: `1px solid ${colors.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            px: 3,
            borderRadius: 0,
            gap: 1.5,
          }}
          role="toolbar"
          aria-label="Save actions"
        >
          <Button onClick={handleCancelEdit} sx={{ ...secondaryButtonSx, border: 'none' }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {isDirty && (
            <Typography sx={{ fontSize: 12, color: colors.textTertiary, mr: 1 }}>
              Unsaved changes
            </Typography>
          )}
          <WriteButton
            variant="outlined"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending || !validPct}
            sx={secondaryButtonSx}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}{' '}
            <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>
              (⌘S)
            </Box>
          </WriteButton>
          <WriteButton
            variant="contained"
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
            disabled={saveMutation.isPending || !validPct}
            onClick={async () => {
              if (isDirty) {
                await saveMutation.mutateAsync().catch(() => null);
              }
              setPublishOpen(true);
            }}
          >
            Save &amp; Publish
          </WriteButton>
        </Paper>
      )}

      <MappingVersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        detail={detail}
      />

      <PublishConfirmationDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        entity="plan_cohort_purse_mapping"
        entities={[
          {
            entity: 'plan_cohort_purse_mapping',
            code: detail.mapping_code,
            name: `${detail.plan_code} · ${detail.cohort_code} → ${detail.purse_code}`,
            current_version: detail.current_version,
            baseline_version: platformVersion,
            changed_fields: [],
            diff: [],
          } as PublishEntity,
        ]}
        onCompleted={(res) => {
          setPublishOpen(false);
          if (res && res.batch_id) {
            setSnackbar({
              open: true,
              msg: `Published ${detail.mapping_code}`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', code] });
            queryClient.invalidateQueries({
              queryKey: ['admin', 'plan-cohort-purse', 'list'],
            });
          }
        }}
      />

      <Dialog
        open={reactivateOpen}
        onClose={() => !reactivateMutation.isPending && setReactivateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Reactivate{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
            {detail.mapping_code}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            It will become available to tenants on next sync.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setReactivateOpen(false)}
            sx={secondaryButtonSx}
            disabled={reactivateMutation.isPending}
          >
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            onClick={() => reactivateMutation.mutate()}
            disabled={reactivateMutation.isPending}
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
          >
            {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </WriteButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MappingEdit;
